import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, Subject } from 'rxjs';
import { UserSettingsResponse } from 'src/app/models/services/users/user-service-response.model';
import { UserSettings } from 'src/app/models/user-settings.model';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../localStorage/localStorage.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private groupeEtablissement = new Subject<string>();
  public currentGE$ = this.groupeEtablissement.asObservable();

  constructor(
    private http: HttpClient,
    private _localStorageService: LocalStorageService
  ) {}

  /**
   * @description
   * Get user settings by user oid.
   *
   * @param userOid
   * @returns An `Observable` containing user settings or null if none exists.
   */
  getUserSettings(userOid: string): Observable<UserSettings | null> {
    return this.http
      .get<UserSettingsResponse>(
        environment.apiUrl + '/settings/' + userOid,
        httpOptions
      )
      .pipe(
        map(response => {
          if (response.userSettings !== null) {
            this.updateCurrentGE(response.userSettings.defaultGE);
            this._localStorageService.setItem(
              'userDefaultDisplayMode',
              response.userSettings.displayModeId
            );
          }
          return response.userSettings;
        })
      );
  }

  /**
   * @description
   * Get user settings:
   * 1. In the local storage when possible;
   * 2. Fallback if necessary on the API.
   *
   * @param userOid
   * @returns An `Observable` containing user settings or null if none exists.
   */
  getUserSettingsFromStorage(userOid: string): Observable<UserSettings | null> {
    const currentGe =
      this._localStorageService.getItem<string>('userDefaultGE');
    const displayMode = this._localStorageService.getItem<number>(
      'userDefaultDisplayMode'
    );
    if (currentGe !== null && displayMode !== null) {
      // this.updateCurrentGE(currentGe);
      return of({
        defaultGE: currentGe,
        displayModeId: displayMode,
      } as UserSettings);
    }
    return this.getUserSettings(userOid);
  }

  /**
   * @description
   * Update user settings
   *
   * @param userSettings User settings
   * @returns
   */
  updateUserSettings(userSettings: UserSettings): Observable<UserSettings> {
    return this.http
      .post<UserSettingsResponse>(
        environment.apiUrl + '/settings/',
        userSettings,
        httpOptions
      )
      .pipe(
        map(response => {
          if (response.userSettings !== null) {
            this.updateCurrentGE(response.userSettings.defaultGE);
            this._localStorageService.setItem(
              'userDefaultDisplayMode',
              response.userSettings.displayModeId
            );
          }
          return response.userSettings;
        })
      );
  }

  /**
   * @description
   * Add user to the settings table.
   *
   * @param userSettings User settings to add.
   * @returns An `Observable` containing the newly created entity.
   */
  createUserSettings(userSettings: UserSettings): Observable<UserSettings> {
    return this.http
      .put<UserSettingsResponse>(
        environment.apiUrl + '/settings/',
        userSettings,
        httpOptions
      )
      .pipe(
        map(response => {
          if (response.userSettings !== null) {
            this.updateCurrentGE(response.userSettings.defaultGE);
            this._localStorageService.setItem(
              'userDefaultDisplayMode',
              response.userSettings.displayModeId
            );
          }
          return response.userSettings;
        })
      );
  }

  updateCurrentGE(ge: string): void {
    if (this._localStorageService.setItem('userDefaultGE', ge)) {
      this.groupeEtablissement.next(ge);
    }
  }
}
